import { c as _c } from "react/compiler-runtime";
// Карточка заявки CRM (/applications/:id) — рабочее место менеджера по одному
// бизнесу: стадия и ответственный, связь в один клик, лента касаний с записью
// звонка и следующим шагом, задачи, данные компании, контакты, где продаёт,
// файлы, кабинет продавца и ИИ-загрузки каталога.
//
// Раньше всё это было модалкой поверх таблицы с телефоном, стадией и историей.
// Стадии, SLA и кабинет по-прежнему живут в домене `applications` — здесь те
// же правила, что были в модалке: «Отклонён» только с причиной, доработка —
// через общую форму замечания.
import { CRM_LEAD_SOURCE_LABELS, type CrmFile } from "@osmix/contracts";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Skeleton } from "@osmix/ui/skeleton";
import { ArrowLeft, ExternalLink, Mail, MessageCircle, Phone, Send, Sparkles, Timer, Undo2, UserPlus } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, APPLICATION_STATUSES, type ApplicationStatus, type SellerApplication } from "@/api";
import { useApplicationCard, useRefreshCard } from "@/api/hooks/use-application-crm";
import { REWORKABLE_STATUSES, useApplicationBoard, useAssignApplication, useMoveApplication } from "@/api/hooks/use-applications";
import { useSellerVerification } from "@/api/hooks/use-sellers";
import { ReworkDialog } from "@/components/application-rework";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { ActivityComposer } from "@/components/crm/activity-composer";
import { CreateAccountBlock } from "@/components/crm/create-account-block";
import { FileDialog, FilesPanel } from "@/components/crm/files-panel";
import { formatDay, formatPhone, mailHref, telegramHref, telHref, whatsappHref } from "@/components/crm/format";
import { AccountPanel, ContactsPanel, DuplicatesNotice, EstimatePanel, ImportsPanel, LeadDataPanel, LinksPanel, SourcePanel, TagsPanel } from "@/components/crm/lead-panels";
import { NextStepPanel, TasksPanel } from "@/components/crm/tasks-panel";
import { Timeline } from "@/components/crm/timeline";
import { PRIORITY_VARIANT, STATUS_ACCENT, Textarea } from "@/components/crm/ui";
import { Select } from "@/components/ui/select";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { cn } from "@/lib/utils";
import { useAuth } from "@/store";
export function ApplicationCardPage() {
  const $ = _c(315);
  const {
    id: t0
  } = useParams();
  const id = t0 === undefined ? "" : t0;
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const user = useAuth(_temp);
  let t1;
  if ($[0] !== user) {
    t1 = hasAdminPermission(user, "action.applications.manage");
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canManage = t1;
  let t2;
  if ($[2] !== user) {
    t2 = hasAdminPermission(user, "page.catalog_import");
    $[2] = user;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const canImport = t2;
  const {
    data: card,
    error,
    isLoading
  } = useApplicationCard(id);
  const board = useApplicationBoard();
  const move = useMoveApplication();
  const assign = useAssignApplication();
  const refreshCard = useRefreshCard(id);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [reworkOpen, setReworkOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [viewingFileId, setViewingFileId] = useState(null);
  let t3;
  if ($[4] !== board.data?.assignees) {
    t3 = board.data?.assignees ?? [];
    $[4] = board.data?.assignees;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const assignees = t3;
  let t4;
  if ($[6] !== board.data?.applications) {
    t4 = [...new Set((board.data?.applications ?? []).flatMap(_temp2))].sort();
    $[6] = board.data?.applications;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const tagSuggestions = t4;
  const verification = useSellerVerification(card?.application.userId ?? "");
  let t5;
  if ($[8] !== verification.data?.checklist) {
    t5 = verification.data?.checklist.map(_temp3);
    $[8] = verification.data?.checklist;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const allowedDocTypes = t5;
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    let t6;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <BackButton />;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    const t7 = notFound ? "text-muted-foreground" : "text-destructive";
    const t8 = notFound ? "\u0417\u0430\u044F\u0432\u043A\u0430 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430 \u2014 \u0432\u043E\u0437\u043C\u043E\u0436\u043D\u043E, \u0435\u0451 \u0443\u0434\u0430\u043B\u0438\u043B\u0438 \u0438\u043B\u0438 \u0441\u0441\u044B\u043B\u043A\u0430 \u0441 \u043E\u0448\u0438\u0431\u043A\u043E\u0439." : error.message;
    let t9;
    if ($[11] !== t7 || $[12] !== t8) {
      t9 = <div className="flex flex-col items-start gap-3">{t6}<p className={t7}>{t8}</p></div>;
      $[11] = t7;
      $[12] = t8;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  if (isLoading || !card) {
    let t6;
    let t7;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Skeleton className="h-8 w-64" />;
      t7 = <Skeleton className="h-28 w-full" />;
      $[14] = t6;
      $[15] = t7;
    } else {
      t6 = $[14];
      t7 = $[15];
    }
    let t8;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="flex flex-col gap-4">{t6}{t7}<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_400px]"><Skeleton className="h-96 w-full" /><Skeleton className="h-96 w-full" /></div></div>;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    return t8;
  }
  const application = card.application;
  let t6;
  if ($[17] !== card.contacts) {
    t6 = card.contacts.find(_temp4) ?? card.contacts[0];
    $[17] = card.contacts;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const primary = t6;
  const phone = application.phone || primary?.phone || "";
  const email = application.email || primary?.email || "";
  let t7;
  if ($[19] !== phone) {
    t7 = telHref(phone);
    $[19] = phone;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const tel = t7;
  let t8;
  if ($[21] !== phone) {
    t8 = whatsappHref(phone);
    $[21] = phone;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const whatsapp = t8;
  const t9 = primary?.telegram;
  let t10;
  if ($[23] !== phone || $[24] !== t9) {
    t10 = telegramHref(t9, phone);
    $[23] = phone;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const telegram = t10;
  let t11;
  if ($[26] !== email) {
    t11 = mailHref(email);
    $[26] = email;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const mail = t11;
  let t12;
  if ($[28] !== application.status) {
    t12 = REWORKABLE_STATUSES.includes(application.status);
    $[28] = application.status;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const canRework = t12;
  let t13;
  if ($[30] !== card.files || $[31] !== viewingFileId) {
    t13 = viewingFileId ? card.files.find(file => file.id === viewingFileId) ?? null : null;
    $[30] = card.files;
    $[31] = viewingFileId;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const viewingFile = t13;
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = file_0 => setViewingFileId(file_0.id);
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const openFile = t14;
  let t15;
  if ($[34] !== application.id || $[35] !== canImport || $[36] !== navigate) {
    t15 = canImport ? () => navigate(`/catalog-import?applicationId=${encodeURIComponent(application.id)}`) : undefined;
    $[34] = application.id;
    $[35] = canImport;
    $[36] = navigate;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const openImport = t15;
  let t16;
  if ($[38] !== application.id || $[39] !== application.status || $[40] !== move || $[41] !== refreshCard) {
    t16 = status => {
      if (status === application.status) {
        return;
      }
      if (status === "rejected") {
        setRejectReason("");
        setRejecting(true);
        return;
      }
      move.mutate({
        id: application.id,
        status
      }, {
        onSettled: refreshCard
      });
    };
    $[38] = application.id;
    $[39] = application.status;
    $[40] = move;
    $[41] = refreshCard;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const requestMove = t16;
  let t17;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Button variant="ghost" size="icon-sm" asChild={true}><Link to="/applications" aria-label={"\u041D\u0430\u0437\u0430\u0434 \u043A \u0437\u0430\u044F\u0432\u043A\u0430\u043C"}><ArrowLeft className="size-4" /></Link></Button>;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== application.shopName) {
    t18 = <p className="truncate text-sm font-semibold">{application.shopName}</p>;
    $[44] = application.shopName;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const t19 = STATUS_ACCENT[application.status];
  let t20;
  if ($[46] !== t19) {
    t20 = cn("size-2 shrink-0 rounded-full", t19);
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t20) {
    t21 = <span className={t20} />;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  const t22 = `applications.status.${application.status}`;
  let t23;
  if ($[50] !== t || $[51] !== t22) {
    t23 = t(t22);
    $[50] = t;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t23) {
    t24 = <span className="truncate">{t23}</span>;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t21 || $[56] !== t24) {
    t25 = <p className="flex items-center gap-1.5 text-xs text-muted-foreground">{t21}{t24}</p>;
    $[55] = t21;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t18 || $[59] !== t25) {
    t26 = <div className="min-w-0 flex-1">{t18}{t25}</div>;
    $[58] = t18;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== tel) {
    t27 = tel && <Button size="sm" asChild={true}><a href={tel}><Phone className="size-4" />Позвонить</a></Button>;
    $[61] = tel;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t26 || $[64] !== t27) {
    t28 = <div className="sticky top-0 z-30 -mx-4 -mt-4 flex items-center gap-2 border-b bg-background/95 px-4 py-2 backdrop-blur md:hidden">{t17}{t26}{t27}</div>;
    $[63] = t26;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="hidden md:block"><BackButton /></div>;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== application.shopName) {
    t30 = <h1 className="text-2xl font-bold tracking-tight break-words">{application.shopName}</h1>;
    $[67] = application.shopName;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== phone) {
    t31 = phone && formatPhone(phone);
    $[69] = phone;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  const t32 = `подана ${formatDay(application.submittedAt)}`;
  const t33 = CRM_LEAD_SOURCE_LABELS[card.profile.source];
  let t34;
  if ($[71] !== application.city || $[72] !== application.contactName || $[73] !== t31 || $[74] !== t32 || $[75] !== t33) {
    t34 = [application.contactName, t31, application.city, t32, t33].filter(Boolean);
    $[71] = application.city;
    $[72] = application.contactName;
    $[73] = t31;
    $[74] = t32;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  const t35 = t34.join(" \xB7 ");
  let t36;
  if ($[77] !== t35) {
    t36 = <p className="text-sm text-muted-foreground">{t35}</p>;
    $[77] = t35;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t30 || $[80] !== t36) {
    t37 = <div className="min-w-0">{t30}{t36}</div>;
    $[79] = t30;
    $[80] = t36;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== application) {
    t38 = <SlaSummary application={application} />;
    $[82] = application;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== t37 || $[85] !== t38) {
    t39 = <div className="flex flex-wrap items-start justify-between gap-3">{t37}{t38}</div>;
    $[84] = t37;
    $[85] = t38;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== t) {
    t40 = t("applications.status.label");
    $[87] = t;
    $[88] = t40;
  } else {
    t40 = $[88];
  }
  let t41;
  if ($[89] !== t40) {
    t41 = <span className="text-xs text-muted-foreground">{t40}</span>;
    $[89] = t40;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  const t42 = STATUS_ACCENT[application.status];
  let t43;
  if ($[91] !== t42) {
    t43 = cn("size-2.5 shrink-0 rounded-full", t42);
    $[91] = t42;
    $[92] = t43;
  } else {
    t43 = $[92];
  }
  let t44;
  if ($[93] !== t43) {
    t44 = <span className={t43} />;
    $[93] = t43;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] !== t) {
    t45 = t("applications.status.label");
    $[95] = t;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  const t46 = application.status;
  const t47 = !canManage || move.isPending;
  let t48;
  if ($[97] !== requestMove) {
    t48 = event => requestMove(event.target.value as ApplicationStatus);
    $[97] = requestMove;
    $[98] = t48;
  } else {
    t48 = $[98];
  }
  let t49;
  if ($[99] !== t) {
    t49 = APPLICATION_STATUSES.map(status_0 => <option key={status_0} value={status_0}>{t(`applications.status.${status_0}`)}</option>);
    $[99] = t;
    $[100] = t49;
  } else {
    t49 = $[100];
  }
  let t50;
  if ($[101] !== application.status || $[102] !== t45 || $[103] !== t47 || $[104] !== t48 || $[105] !== t49) {
    t50 = <Select aria-label={t45} className="w-48" value={t46} disabled={t47} onChange={t48}>{t49}</Select>;
    $[101] = application.status;
    $[102] = t45;
    $[103] = t47;
    $[104] = t48;
    $[105] = t49;
    $[106] = t50;
  } else {
    t50 = $[106];
  }
  let t51;
  if ($[107] !== t44 || $[108] !== t50) {
    t51 = <span className="flex items-center gap-2">{t44}{t50}</span>;
    $[107] = t44;
    $[108] = t50;
    $[109] = t51;
  } else {
    t51 = $[109];
  }
  let t52;
  if ($[110] !== t41 || $[111] !== t51) {
    t52 = <label className="flex min-w-0 flex-col gap-1">{t41}{t51}</label>;
    $[110] = t41;
    $[111] = t51;
    $[112] = t52;
  } else {
    t52 = $[112];
  }
  let t53;
  if ($[113] !== t) {
    t53 = t("applications.assignee");
    $[113] = t;
    $[114] = t53;
  } else {
    t53 = $[114];
  }
  let t54;
  if ($[115] !== t53) {
    t54 = <span className="text-xs text-muted-foreground">{t53}</span>;
    $[115] = t53;
    $[116] = t54;
  } else {
    t54 = $[116];
  }
  let t55;
  if ($[117] !== t) {
    t55 = t("applications.assignee");
    $[117] = t;
    $[118] = t55;
  } else {
    t55 = $[118];
  }
  const t56 = application.assignee?.id ?? "";
  const t57 = !canManage || assign.isPending;
  let t58;
  if ($[119] !== application.id || $[120] !== assign || $[121] !== refreshCard) {
    t58 = event_0 => assign.mutate({
      id: application.id,
      assigneeId: event_0.target.value || null
    }, {
      onSettled: refreshCard
    });
    $[119] = application.id;
    $[120] = assign;
    $[121] = refreshCard;
    $[122] = t58;
  } else {
    t58 = $[122];
  }
  let t59;
  if ($[123] !== t) {
    t59 = t("applications.unassigned");
    $[123] = t;
    $[124] = t59;
  } else {
    t59 = $[124];
  }
  let t60;
  if ($[125] !== t59) {
    t60 = <option value="">{t59}</option>;
    $[125] = t59;
    $[126] = t60;
  } else {
    t60 = $[126];
  }
  let t61;
  if ($[127] !== application.assignee || $[128] !== assignees) {
    t61 = application.assignee && !assignees.some(item_1 => item_1.id === application.assignee?.id) && <option value={application.assignee.id}>{application.assignee.name}</option>;
    $[127] = application.assignee;
    $[128] = assignees;
    $[129] = t61;
  } else {
    t61 = $[129];
  }
  let t62;
  if ($[130] !== assignees) {
    t62 = assignees.map(_temp5);
    $[130] = assignees;
    $[131] = t62;
  } else {
    t62 = $[131];
  }
  let t63;
  if ($[132] !== t55 || $[133] !== t56 || $[134] !== t57 || $[135] !== t58 || $[136] !== t60 || $[137] !== t61 || $[138] !== t62) {
    t63 = <Select aria-label={t55} className="w-56" value={t56} disabled={t57} onChange={t58}>{t60}{t61}{t62}</Select>;
    $[132] = t55;
    $[133] = t56;
    $[134] = t57;
    $[135] = t58;
    $[136] = t60;
    $[137] = t61;
    $[138] = t62;
    $[139] = t63;
  } else {
    t63 = $[139];
  }
  let t64;
  if ($[140] !== t54 || $[141] !== t63) {
    t64 = <label className="flex min-w-0 flex-col gap-1">{t54}{t63}</label>;
    $[140] = t54;
    $[141] = t63;
    $[142] = t64;
  } else {
    t64 = $[142];
  }
  let t65;
  if ($[143] !== canManage || $[144] !== canRework || $[145] !== t) {
    t65 = canManage && canRework && <Button variant="outline" onClick={() => setReworkOpen(true)}><Undo2 className="size-4" />{t("applications.rework.action")}</Button>;
    $[143] = canManage;
    $[144] = canRework;
    $[145] = t;
    $[146] = t65;
  } else {
    t65 = $[146];
  }
  let t66;
  if ($[147] !== t52 || $[148] !== t64 || $[149] !== t65) {
    t66 = <div className="flex flex-wrap items-end gap-3">{t52}{t64}{t65}</div>;
    $[147] = t52;
    $[148] = t64;
    $[149] = t65;
    $[150] = t66;
  } else {
    t66 = $[150];
  }
  let t67;
  if ($[151] !== assign.error || $[152] !== assign.isError || $[153] !== move.error || $[154] !== move.isError) {
    t67 = (move.isError || assign.isError) && <p className="text-sm text-destructive">{move.isError ? (move.error as Error).message : (assign.error as Error).message}</p>;
    $[151] = assign.error;
    $[152] = assign.isError;
    $[153] = move.error;
    $[154] = move.isError;
    $[155] = t67;
  } else {
    t67 = $[155];
  }
  let t68;
  if ($[156] !== tel) {
    t68 = tel && <Button asChild={true}><a href={tel}><Phone className="size-4" />Позвонить</a></Button>;
    $[156] = tel;
    $[157] = t68;
  } else {
    t68 = $[157];
  }
  let t69;
  if ($[158] !== mail) {
    t69 = mail && <Button variant="outline" asChild={true}><a href={mail}><Mail className="size-4" />Письмо</a></Button>;
    $[158] = mail;
    $[159] = t69;
  } else {
    t69 = $[159];
  }
  let t70;
  if ($[160] !== whatsapp) {
    t70 = whatsapp && <Button variant="outline" asChild={true}><a href={whatsapp} target="_blank" rel="noreferrer"><MessageCircle className="size-4" />WhatsApp</a></Button>;
    $[160] = whatsapp;
    $[161] = t70;
  } else {
    t70 = $[161];
  }
  let t71;
  if ($[162] !== telegram) {
    t71 = telegram && <Button variant="outline" asChild={true}><a href={telegram} target="_blank" rel="noreferrer"><Send className="size-4" />Telegram</a></Button>;
    $[162] = telegram;
    $[163] = t71;
  } else {
    t71 = $[163];
  }
  let t72;
  if ($[164] !== application.userId || $[165] !== canManage || $[166] !== card.account) {
    t72 = card.account ? <Button variant="outline" asChild={true}><Link to={`/shops/seller/${card.account.userId}`}><ExternalLink className="size-4" />Кабинет продавца</Link></Button> : canManage && !application.userId && <Button variant="outline" onClick={() => setAccountOpen(true)}><UserPlus className="size-4" />Создать кабинет продавца</Button>;
    $[164] = application.userId;
    $[165] = canManage;
    $[166] = card.account;
    $[167] = t72;
  } else {
    t72 = $[167];
  }
  let t73;
  if ($[168] !== openImport) {
    t73 = openImport && <Button variant="outline" onClick={openImport}><Sparkles className="size-4" />Загрузить каталог (ИИ)</Button>;
    $[168] = openImport;
    $[169] = t73;
  } else {
    t73 = $[169];
  }
  let t74;
  if ($[170] !== t68 || $[171] !== t69 || $[172] !== t70 || $[173] !== t71 || $[174] !== t72 || $[175] !== t73) {
    t74 = <div className="flex flex-wrap gap-2" data-testid="crm-quick-actions">{t68}{t69}{t70}{t71}{t72}{t73}</div>;
    $[170] = t68;
    $[171] = t69;
    $[172] = t70;
    $[173] = t71;
    $[174] = t72;
    $[175] = t73;
    $[176] = t74;
  } else {
    t74 = $[176];
  }
  let t75;
  if ($[177] !== t39 || $[178] !== t66 || $[179] !== t67 || $[180] !== t74) {
    t75 = <header className="flex flex-col gap-3" data-testid="crm-card-header">{t29}{t39}{t66}{t67}{t74}</header>;
    $[177] = t39;
    $[178] = t66;
    $[179] = t67;
    $[180] = t74;
    $[181] = t75;
  } else {
    t75 = $[181];
  }
  let t76;
  if ($[182] !== card.duplicates) {
    t76 = <DuplicatesNotice duplicates={card.duplicates} />;
    $[182] = card.duplicates;
    $[183] = t76;
  } else {
    t76 = $[183];
  }
  let t77;
  if ($[184] !== application.id || $[185] !== canManage || $[186] !== card.nextTask || $[187] !== card.tasks) {
    t77 = canManage && <ActivityComposer applicationId={application.id} nextTask={card.nextTask} openTasks={card.tasks.filter(_temp6).length} />;
    $[184] = application.id;
    $[185] = canManage;
    $[186] = card.nextTask;
    $[187] = card.tasks;
    $[188] = t77;
  } else {
    t77 = $[188];
  }
  const t78 = user?.id ?? null;
  const t79 = user?.role === "admin";
  let t80;
  if ($[189] !== application.id || $[190] !== canManage || $[191] !== card.files || $[192] !== card.timeline || $[193] !== t78 || $[194] !== t79) {
    t80 = <Timeline applicationId={application.id} items={card.timeline} files={card.files} currentUserId={t78} isAdmin={t79} canManage={canManage} onOpenFile={openFile} />;
    $[189] = application.id;
    $[190] = canManage;
    $[191] = card.files;
    $[192] = card.timeline;
    $[193] = t78;
    $[194] = t79;
    $[195] = t80;
  } else {
    t80 = $[195];
  }
  let t81;
  if ($[196] !== t77 || $[197] !== t80) {
    t81 = <div className="flex min-w-0 flex-col gap-4">{t77}{t80}</div>;
    $[196] = t77;
    $[197] = t80;
    $[198] = t81;
  } else {
    t81 = $[198];
  }
  let t82;
  if ($[199] !== application.id || $[200] !== assignees || $[201] !== canManage || $[202] !== card.nextTask) {
    t82 = <NextStepPanel applicationId={application.id} task={card.nextTask} assignees={assignees} canManage={canManage} />;
    $[199] = application.id;
    $[200] = assignees;
    $[201] = canManage;
    $[202] = card.nextTask;
    $[203] = t82;
  } else {
    t82 = $[203];
  }
  let t83;
  if ($[204] !== application.id || $[205] !== assignees || $[206] !== canManage || $[207] !== card.tasks) {
    t83 = <TasksPanel applicationId={application.id} tasks={card.tasks} assignees={assignees} canManage={canManage} />;
    $[204] = application.id;
    $[205] = assignees;
    $[206] = canManage;
    $[207] = card.tasks;
    $[208] = t83;
  } else {
    t83 = $[208];
  }
  let t84;
  if ($[209] !== application || $[210] !== canManage || $[211] !== card.profile) {
    t84 = <LeadDataPanel application={application} profile={card.profile} canManage={canManage} />;
    $[209] = application;
    $[210] = canManage;
    $[211] = card.profile;
    $[212] = t84;
  } else {
    t84 = $[212];
  }
  let t85;
  if ($[213] !== application.contactName || $[214] !== application.email || $[215] !== application.phone) {
    t85 = {
      name: application.contactName,
      phone: application.phone,
      email: application.email
    };
    $[213] = application.contactName;
    $[214] = application.email;
    $[215] = application.phone;
    $[216] = t85;
  } else {
    t85 = $[216];
  }
  let t86;
  if ($[217] !== application.id || $[218] !== canManage || $[219] !== card.contacts || $[220] !== t85) {
    t86 = <ContactsPanel applicationId={application.id} contacts={card.contacts} canManage={canManage} main={t85} />;
    $[217] = application.id;
    $[218] = canManage;
    $[219] = card.contacts;
    $[220] = t85;
    $[221] = t86;
  } else {
    t86 = $[221];
  }
  let t87;
  if ($[222] !== application.id || $[223] !== canManage || $[224] !== card.profile.links) {
    t87 = <LinksPanel applicationId={application.id} links={card.profile.links} canManage={canManage} />;
    $[222] = application.id;
    $[223] = canManage;
    $[224] = card.profile.links;
    $[225] = t87;
  } else {
    t87 = $[225];
  }
  let t88;
  if ($[226] !== application.id || $[227] !== canManage || $[228] !== card.profile) {
    t88 = <EstimatePanel applicationId={application.id} profile={card.profile} canManage={canManage} />;
    $[226] = application.id;
    $[227] = canManage;
    $[228] = card.profile;
    $[229] = t88;
  } else {
    t88 = $[229];
  }
  let t89;
  if ($[230] !== application.id || $[231] !== canManage || $[232] !== card.profile.tags || $[233] !== tagSuggestions) {
    t89 = <TagsPanel applicationId={application.id} tags={card.profile.tags} suggestions={tagSuggestions} canManage={canManage} />;
    $[230] = application.id;
    $[231] = canManage;
    $[232] = card.profile.tags;
    $[233] = tagSuggestions;
    $[234] = t89;
  } else {
    t89 = $[234];
  }
  let t90;
  if ($[235] !== application.id || $[236] !== application.submittedAt || $[237] !== canManage || $[238] !== card.profile) {
    t90 = <SourcePanel applicationId={application.id} profile={card.profile} submittedAt={application.submittedAt} canManage={canManage} />;
    $[235] = application.id;
    $[236] = application.submittedAt;
    $[237] = canManage;
    $[238] = card.profile;
    $[239] = t90;
  } else {
    t90 = $[239];
  }
  let t91;
  if ($[240] !== application.id || $[241] !== canManage || $[242] !== card.files) {
    t91 = <FilesPanel applicationId={application.id} files={card.files} canManage={canManage} onOpen={openFile} />;
    $[240] = application.id;
    $[241] = canManage;
    $[242] = card.files;
    $[243] = t91;
  } else {
    t91 = $[243];
  }
  let t92;
  if ($[244] !== application.userId || $[245] !== canManage) {
    t92 = canManage && !application.userId ? () => setAccountOpen(true) : undefined;
    $[244] = application.userId;
    $[245] = canManage;
    $[246] = t92;
  } else {
    t92 = $[246];
  }
  let t93;
  if ($[247] !== card.account || $[248] !== t92) {
    t93 = <AccountPanel account={card.account} onCreate={t92} />;
    $[247] = card.account;
    $[248] = t92;
    $[249] = t93;
  } else {
    t93 = $[249];
  }
  let t94;
  if ($[250] !== card.importJobs || $[251] !== openImport) {
    t94 = <ImportsPanel jobs={card.importJobs} onNew={openImport} />;
    $[250] = card.importJobs;
    $[251] = openImport;
    $[252] = t94;
  } else {
    t94 = $[252];
  }
  let t95;
  if ($[253] !== t82 || $[254] !== t83 || $[255] !== t84 || $[256] !== t86 || $[257] !== t87 || $[258] !== t88 || $[259] !== t89 || $[260] !== t90 || $[261] !== t91 || $[262] !== t93 || $[263] !== t94) {
    t95 = <aside className="flex min-w-0 flex-col gap-4" aria-label={"\u0414\u0430\u043D\u043D\u044B\u0435 \u0437\u0430\u044F\u0432\u043A\u0438"}>{t82}{t83}{t84}{t86}{t87}{t88}{t89}{t90}{t91}{t93}{t94}</aside>;
    $[253] = t82;
    $[254] = t83;
    $[255] = t84;
    $[256] = t86;
    $[257] = t87;
    $[258] = t88;
    $[259] = t89;
    $[260] = t90;
    $[261] = t91;
    $[262] = t93;
    $[263] = t94;
    $[264] = t95;
  } else {
    t95 = $[264];
  }
  let t96;
  if ($[265] !== t81 || $[266] !== t95) {
    t96 = <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_400px]">{t81}{t95}</div>;
    $[265] = t81;
    $[266] = t95;
    $[267] = t96;
  } else {
    t96 = $[267];
  }
  let t97;
  if ($[268] === Symbol.for("react.memo_cache_sentinel")) {
    t97 = () => setViewingFileId(null);
    $[268] = t97;
  } else {
    t97 = $[268];
  }
  let t98;
  if ($[269] !== application.id || $[270] !== canManage || $[271] !== viewingFile) {
    t98 = <FileDialog applicationId={application.id} file={viewingFile} canManage={canManage} onClose={t97} />;
    $[269] = application.id;
    $[270] = canManage;
    $[271] = viewingFile;
    $[272] = t98;
  } else {
    t98 = $[272];
  }
  let t99;
  if ($[273] !== application.shopName) {
    t99 = <DialogHeader><DialogTitle>Кабинет продавца для «{application.shopName}»</DialogTitle></DialogHeader>;
    $[273] = application.shopName;
    $[274] = t99;
  } else {
    t99 = $[274];
  }
  let t100;
  if ($[275] === Symbol.for("react.memo_cache_sentinel")) {
    t100 = () => setAccountOpen(false);
    $[275] = t100;
  } else {
    t100 = $[275];
  }
  let t101;
  if ($[276] !== application || $[277] !== refreshCard) {
    t101 = <CreateAccountBlock application={application} defaultOpen={true} onCancel={t100} onCreated={refreshCard} />;
    $[276] = application;
    $[277] = refreshCard;
    $[278] = t101;
  } else {
    t101 = $[278];
  }
  let t102;
  if ($[279] !== t101 || $[280] !== t99) {
    t102 = <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">{t99}{t101}</DialogContent>;
    $[279] = t101;
    $[280] = t99;
    $[281] = t102;
  } else {
    t102 = $[281];
  }
  let t103;
  if ($[282] !== accountOpen || $[283] !== t102) {
    t103 = <Dialog open={accountOpen} onOpenChange={setAccountOpen}>{t102}</Dialog>;
    $[282] = accountOpen;
    $[283] = t102;
    $[284] = t103;
  } else {
    t103 = $[284];
  }
  let t104;
  if ($[285] === Symbol.for("react.memo_cache_sentinel")) {
    t104 = () => setReworkOpen(false);
    $[285] = t104;
  } else {
    t104 = $[285];
  }
  let t105;
  if ($[286] !== allowedDocTypes || $[287] !== application.id || $[288] !== application.shopName || $[289] !== refreshCard || $[290] !== reworkOpen) {
    t105 = <ReworkDialog open={reworkOpen} applicationId={application.id} shopName={application.shopName} allowedDocTypes={allowedDocTypes} onClose={t104} onDone={refreshCard} />;
    $[286] = allowedDocTypes;
    $[287] = application.id;
    $[288] = application.shopName;
    $[289] = refreshCard;
    $[290] = reworkOpen;
    $[291] = t105;
  } else {
    t105 = $[291];
  }
  let t106;
  if ($[292] === Symbol.for("react.memo_cache_sentinel")) {
    t106 = () => setRejecting(false);
    $[292] = t106;
  } else {
    t106 = $[292];
  }
  let t107;
  if ($[293] !== application.id || $[294] !== move || $[295] !== refreshCard || $[296] !== rejectReason) {
    t107 = () => {
      if (!rejectReason.trim()) {
        return;
      }
      move.mutate({
        id: application.id,
        status: "rejected",
        reason: rejectReason.trim()
      }, {
        onSettled: () => {
          setRejecting(false);
          refreshCard();
        }
      });
    };
    $[293] = application.id;
    $[294] = move;
    $[295] = refreshCard;
    $[296] = rejectReason;
    $[297] = t107;
  } else {
    t107 = $[297];
  }
  let t108;
  if ($[298] === Symbol.for("react.memo_cache_sentinel")) {
    t108 = e => setRejectReason(e.target.value);
    $[298] = t108;
  } else {
    t108 = $[298];
  }
  let t109;
  if ($[299] !== rejectReason) {
    t109 = <Textarea value={rejectReason} aria-label={"\u041F\u0440\u0438\u0447\u0438\u043D\u0430 \u043E\u0442\u043A\u0430\u0437\u0430"} onChange={t108} placeholder={"\u041F\u0440\u0438\u0447\u0438\u043D\u0430 \u043E\u0442\u043A\u0430\u0437\u0430 \u2014 \u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u044C\u043D\u043E"} className="min-h-24" />;
    $[299] = rejectReason;
    $[300] = t109;
  } else {
    t109 = $[300];
  }
  let t110;
  if ($[301] !== move.isPending || $[302] !== rejecting || $[303] !== t107 || $[304] !== t109) {
    t110 = <ConfirmDialog open={rejecting} title={"\u041E\u0442\u043A\u043B\u043E\u043D\u0438\u0442\u044C \u0437\u0430\u044F\u0432\u043A\u0443?"} description={"\u041F\u0440\u043E\u0434\u0430\u0432\u0435\u0446 \u043F\u043E\u043B\u0443\u0447\u0438\u0442 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435 \u0441 \u043F\u0440\u0438\u0447\u0438\u043D\u043E\u0439. \u041F\u043E\u0432\u0442\u043E\u0440\u043D\u043E \u043F\u043E\u0434\u0430\u0442\u044C \u0437\u0430\u044F\u0432\u043A\u0443 \u043E\u043D \u0441\u043C\u043E\u0436\u0435\u0442 \u0442\u043E\u043B\u044C\u043A\u043E \u0447\u0435\u0440\u0435\u0437 90 \u0434\u043D\u0435\u0439."} confirmLabel={"\u041E\u0442\u043A\u043B\u043E\u043D\u0438\u0442\u044C"} confirmVariant="destructive" busy={move.isPending} onClose={t106} onConfirm={t107}>{t109}</ConfirmDialog>;
    $[301] = move.isPending;
    $[302] = rejecting;
    $[303] = t107;
    $[304] = t109;
    $[305] = t110;
  } else {
    t110 = $[305];
  }
  let t111;
  if ($[306] !== t103 || $[307] !== t105 || $[308] !== t110 || $[309] !== t28 || $[310] !== t75 || $[311] !== t76 || $[312] !== t96 || $[313] !== t98) {
    t111 = <div className="flex flex-col gap-4">{t28}{t75}{t76}{t96}{t98}{t103}{t105}{t110}</div>;
    $[306] = t103;
    $[307] = t105;
    $[308] = t110;
    $[309] = t28;
    $[310] = t75;
    $[311] = t76;
    $[312] = t96;
    $[313] = t98;
    $[314] = t111;
  } else {
    t111 = $[314];
  }
  return t111;
}
function _temp6(task) {
  return !task.doneAt;
}
function _temp5(assignee) {
  return <option key={assignee.id} value={assignee.id}>{assignee.name}</option>;
}
function _temp4(item_0) {
  return item_0.isPrimary;
}
function _temp3(item) {
  return item.docType;
}
function _temp2(entry) {
  return entry.tags ?? [];
}
function _temp(s) {
  return s.user;
}
function BackButton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Button variant="ghost" size="sm" className="-ml-2" asChild={true}><Link to="/applications"><ArrowLeft className="size-4" />Назад к заявкам</Link></Button>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Сроки с сервера (дни в стадии по Москве, SLA, приоритет) — консоль их только показывает. */
function SlaSummary(t0) {
  const $ = _c(29);
  const {
    application
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    daysInStage,
    daysLeft,
    overdue,
    priority,
    severity,
    slaDays
  } = application.sla;
  const t1 = severity === "error" && "border-destructive/40 bg-destructive/10 text-destructive";
  const t2 = severity === "warning" && "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-500";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn("flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-sm", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Timer className="size-4 shrink-0" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== daysInStage || $[5] !== t) {
    t5 = t("applications.daysInStage", {
      count: daysInStage
    });
    $[4] = daysInStage;
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <span><span className="font-semibold tabular-nums">{t5}</span> в стадии</span>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== daysLeft || $[10] !== overdue || $[11] !== severity || $[12] !== t) {
    t7 = daysLeft !== null && <span className={cn(!severity && (overdue ? "text-destructive" : "text-muted-foreground"))}>· {overdue ? t("applications.sla.overdue") : t("applications.sla.daysLeft", {
        count: daysLeft
      })}</span>;
    $[9] = daysLeft;
    $[10] = overdue;
    $[11] = severity;
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = PRIORITY_VARIANT[priority];
  let t9;
  if ($[14] !== slaDays || $[15] !== t) {
    t9 = t("applications.priorityAuto", {
      sla: slaDays
    });
    $[14] = slaDays;
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = `applications.priority.${priority}`;
  let t11;
  if ($[17] !== t || $[18] !== t10) {
    t11 = t(t10);
    $[17] = t;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t8 || $[22] !== t9) {
    t12 = <Badge variant={t8} title={t9}>{t11}</Badge>;
    $[20] = t11;
    $[21] = t8;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t3 || $[26] !== t6 || $[27] !== t7) {
    t13 = <div data-testid="crm-sla" className={t3}>{t4}{t6}{t7}{t12}</div>;
    $[24] = t12;
    $[25] = t3;
    $[26] = t6;
    $[27] = t7;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
}